// /srv/docker/develop/strike.hu_51200-51220/next/components/pages/IndoorActivitiesPage.jsx

"use client";

import {
    CloudRain,
    Target,
    Users,
    Sparkles,
    CalendarCheck,
    MapPin,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

const translations = {
    hu: {
        heroTitle:
            "beltéri program Budapesten – próbáld ki a strike-ot",

        heroSubtitle: (
            <>
                odakint lehet bármilyen idő.
                <br />
                idebent kezdődik a játék.
            </>
        ),

        heroDescription:
            "ha olyan benti programot keresel Budapesten, ami nem függ az időjárástól, próbáld ki a strike interaktív ügyességi élményét.",

        heroSecond:
            "8 különleges pálya, 48 dobás és egy közös kihívás vár rád barátokkal, családdal, pároddal vagy kollégákkal.",

        bookingButton:
            "időpontot foglalok",

        heroImageAlt:
            "strike beltéri ügyességi program Budapesten",

        weatherTitle:
            "program esős időben Budapesten",

        weatherFirst:
            "esik az eső? hideg van? túl meleg van odakint? a strike teljesen beltéri program, ezért az időjárás nem szólhat bele a terveitekbe.",

        weatherSecond:
            "nem kell az előrejelzést figyelni, és nem kell B tervet keresni. csak válasszatok egy időpontot, és jöhet a játék.",

        weatherThird:
            "ősszel, télen, tavasszal és nyáron is ugyanaz a 8 pálya és 48 dobás vár benneteket.",

        experienceTitle:
            "nem csak egy benti program",

        experienceIntroduction:
            "a strike aktív, interaktív élmény, ahol végig történik valami.",

        experienceItems: [
            {
                title:
                    "8 különleges pálya",

                description:
                    "minden pálya más ügyességi kihívást jelent.",
            },
            {
                title:
                    "48 dobás",

                description:
                    "minden pályán 6 dobásod van, így egy teljes játék során összesen 48 alkalommal próbálhatod eltalálni a célokat.",
            },
            {
                title:
                    "pontverseny",

                description:
                    "a találatok pontokat érnek, az eredmények pedig végig követhetők.",
            },
            {
                title:
                    "közös élmény",

                description:
                    "játsszatok együtt, szurkoljatok egymásnak és derüljön ki, ki céloz a legpontosabban.",
            },
        ],

        whoTitle:
            "kivel jöhetsz?",

        whoIntroduction:
            "szinte bármilyen társasággal működik.",

        whoItems: [
            "barátokkal",
            "családdal",
            "pároddal",
            "kollégákkal",
            "születésnapi társasággal",
            "céges csapattal",
        ],

        friendsTitle:
            "program barátokkal",

        friendsDescription:
            "ha már unjátok a mozit, a beülést vagy ugyanazokat a hétvégi programokat, a strike egy aktívabb közös élmény.",

        friendsSecond:
            "néhány dobás után garantáltan elkezdődik a verseny, és mindenki pontosan tudni fogja, ki áll előtte a rangsorban.",

        friendsButton:
            "program barátokkal",

        birthdayTitle:
            "szülinapi program beltérben",

        birthdayDescription:
            "egy születésnapot sem kell az időjárásra bízni. a strike-ban egész évben együtt játszhattok, és ha saját teret is szeretnétek, ott a private room.",

        birthdayButton:
            "szülinapi program",

        companyTitle:
            "beltéri csapatépítő",

        companyDescription:
            "céges program szervezésénél különösen jól jön, ha az időjárás nem tényező. a strike egyszerre aktív program és közös verseny a csapatnak.",

        companyButton:
            "csapatépítő program",

        differentTitle:
            "ha valami újat keresel Budapesten",

        differentFirst:
            "a strike nem bowling, nem szabadulószoba és nem virtuális játék.",

        differentSecond:
            "itt te dobod el a mágneses nyilat, te célzol, és rajtad múlik, hogy melyik célfelületet sikerül eltalálnod.",

        differentThird:
            "egyszerű szabályok, valódi mozgás és egy olyan program, amit együtt éltek át.",

        locationTitle:
            "beltéri program a XIII. kerületben",

        locationDescription:
            "a strike Budapest XIII. kerületében, a Pannónia utca 46/A alatt található, könnyen megközelíthető helyen.",

        locationSecond:
            "a Westend, a Lehel tér és a Jászai Mari tér környékéről is könnyen elérhető.",

        locationButton:
            "megközelítés",

        finalTitle:
            "nem kell megvárni a jó időt",

        finalDescription:
            "8 pálya. 48 dobás. egy beltéri élmény Budapesten.",

        finalSecond:
            "odakint lehet bármilyen idő. idebent kezdődik a játék.",

        finalButton:
            "foglalj időpontot",

        secondImageAlt:
            "interaktív beltéri ügyességi program a strike-ban Budapesten",
    },

    en: {
        heroTitle:
            "indoor activity in Budapest",

        heroSubtitle: (
            <>
                whatever the weather outside.
                <br />
                the game starts inside.
            </>
        ),

        heroDescription:
            "if you're looking for an indoor activity in Budapest that doesn't depend on the weather, try strike's interactive skill experience.",

        heroSecond:
            "8 unique courses, 48 throws and a shared challenge await you with friends, family, your partner or colleagues.",

        bookingButton:
            "book now",

        heroImageAlt:
            "indoor interactive activity at strike in Budapest",

        weatherTitle:
            "things to do in Budapest when it rains",

        weatherFirst:
            "rainy, cold or too hot outside? strike is completely indoors, so the weather doesn't have to change your plans.",

        weatherSecond:
            "there's no need to check the forecast or look for a backup plan. simply choose a time and start playing.",

        weatherThird:
            "the same 8 courses and 48 throws are waiting for you all year round.",

        experienceTitle:
            "more than just an indoor activity",

        experienceIntroduction:
            "strike is an active, interactive experience where something is always happening.",

        experienceItems: [
            {
                title:
                    "8 unique courses",

                description:
                    "each course offers a different skill challenge.",
            },
            {
                title:
                    "48 throws",

                description:
                    "you get 6 throws on every course, for a total of 48 attempts during a full game.",
            },
            {
                title:
                    "score competition",

                description:
                    "hits earn points and you can follow the scores throughout the game.",
            },
            {
                title:
                    "shared experience",

                description:
                    "play together, cheer each other on and find out who has the best aim.",
            },
        ],

        whoTitle:
            "who can you come with?",

        whoIntroduction:
            "strike works with almost any group.",

        whoItems: [
            "friends",
            "family",
            "your partner",
            "colleagues",
            "birthday groups",
            "corporate teams",
        ],

        friendsTitle:
            "an activity with friends",

        friendsDescription:
            "if you're tired of the cinema, sitting in a bar or doing the same weekend activities, strike offers something more active.",

        friendsSecond:
            "after a few throws, the competition starts naturally and everyone knows exactly who's ahead.",

        friendsButton:
            "activity with friends",

        birthdayTitle:
            "indoor birthday activity",

        birthdayDescription:
            "your birthday plans don't have to depend on the weather. play together all year round, and if you'd like your own space, choose the private room.",

        birthdayButton:
            "birthday experience",

        companyTitle:
            "indoor team building",

        companyDescription:
            "when organising a corporate event, it helps if the weather isn't a factor. strike combines an active activity with friendly competition.",

        companyButton:
            "team-building activity",

        differentTitle:
            "if you're looking for something new in Budapest",

        differentFirst:
            "strike isn't bowling, an escape room or a virtual game.",

        differentSecond:
            "you throw the magnetic dart, you aim and it's up to you which target you manage to hit.",

        differentThird:
            "simple rules, real movement and an experience you share together.",

        locationTitle:
            "indoor activity in District XIII",

        locationDescription:
            "strike is located at Pannónia utca 46/A in Budapest's District XIII, in an easily accessible location.",

        locationSecond:
            "it's easy to reach from the Westend, Lehel tér and Jászai Mari tér areas.",

        locationButton:
            "getting here",

        finalTitle:
            "no need to wait for good weather",

        finalDescription:
            "8 courses. 48 throws. one indoor experience in Budapest.",

        finalSecond:
            "whatever the weather outside. the game starts inside.",

        finalButton:
            "book now",

        secondImageAlt:
            "interactive indoor skill activity at strike in Budapest",
    },
};

export default function BelteriProgramBudapestPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* HERO */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex min-h-[500px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[620px] md:px-12 md:py-20">
                    <IconCircle>
                        <CloudRain
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </IconCircle>

                    <h1 className="text-[27px] font-black lowercase leading-[1.05] md:text-[50px]">
                        {text.heroTitle}
                    </h1>

                    <p className="mt-8 text-[19px] font-black leading-[1.15] md:text-[27px]">
                        {text.heroSubtitle}
                    </p>

                    <p className="mt-7 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroDescription}
                    </p>

                    <p className="mt-6 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroSecond}
                    </p>

                    <PrimaryButton
                        href="/booking"
                    >
                        {text.bookingButton}
                    </PrimaryButton>
                </div>
            </section>

            <PageImage
                src="/v4/megerkeztel_strike.webp"
                alt={
                    text.heroImageAlt
                }
            />

            {/* ESŐS IDŐ */}
            <ContentSection
                icon={
                    CloudRain
                }
                title={
                    text.weatherTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.weatherFirst}
                    </p>

                    <p>
                        {text.weatherSecond}
                    </p>

                    <p>
                        {text.weatherThird}
                    </p>
                </div>
            </ContentSection>

            {/* MITŐL MÁS */}
            <section className="bg-[#f2f2f2] text-black">
                <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                    <div className="border-[3px] border-black bg-[#f2f2f2]">
                        <div className="bg-black px-6 py-9 text-center text-white md:px-12 md:py-12">
                            <Target
                                className="mx-auto h-10 w-10 md:h-12 md:w-12"
                                strokeWidth={
                                    2.5
                                }
                            />

                            <h2 className="mt-5 text-[24px] font-black lowercase leading-none md:text-[42px]">
                                {text.experienceTitle}
                            </h2>

                            <p className="mx-auto mt-5 max-w-[300px] text-[14px] leading-[1.3] text-white/70 md:max-w-xl md:text-[19px]">
                                {text.experienceIntroduction}
                            </p>
                        </div>

                        <div className="px-5 py-8 md:px-12 md:py-12">
                            <div className="border-y-[3px] border-black">
                                {text.experienceItems.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <div
                                            key={
                                                item.title
                                            }
                                            className={`flex items-start gap-4 py-5 md:gap-6 md:py-7 ${index !==
                                                text.experienceItems.length - 1
                                                ? "border-b border-black/25"
                                                : ""
                                                }`}
                                        >
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                                {String(
                                                    index +
                                                    1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </span>

                                            <div>
                                                <h3 className="text-[16px] font-black lowercase leading-[1.2] md:text-[21px]">
                                                    {item.title}
                                                </h3>

                                                <p className="mt-2 text-[14px] leading-[1.4] text-neutral-700 md:text-[18px] md:leading-[1.5]">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PageImage
                src="/v4/találd_meg_eldoradot.webp"
                alt={
                    text.secondImageAlt
                }
            />

            {/* KIVEL */}
            <ListSection
                icon={
                    Users
                }
                title={
                    text.whoTitle
                }
                introduction={
                    text.whoIntroduction
                }
                items={
                    text.whoItems
                }
            />

            {/* BARÁTOKKAL */}
            <ContentSection
                icon={
                    Users
                }
                title={
                    text.friendsTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.friendsDescription}
                    </p>

                    <p>
                        {text.friendsSecond}
                    </p>
                </div>

                <SecondaryButton
                    href={getLocalizedPath(
                        "/program-baratokkal-budapest",
                        language
                    )}
                >
                    {text.friendsButton}
                </SecondaryButton>
            </ContentSection>

            {/* SZÜLINAP */}
            <ContentSection
                icon={
                    Sparkles
                }
                title={
                    text.birthdayTitle
                }
            >
                <p className="mx-auto mt-8 max-w-[330px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    {text.birthdayDescription}
                </p>

                <PrimaryButton
                    href={getLocalizedPath(
                        "/szulinap-budapest",
                        language
                    )}
                >
                    {text.birthdayButton}
                </PrimaryButton>
            </ContentSection>

            {/* CÉGES */}
            <ContentSection
                icon={
                    Users
                }
                title={
                    text.companyTitle
                }
                backgroundClass="bg-white"
            >
                <p className="mx-auto mt-8 max-w-[330px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    {text.companyDescription}
                </p>

                <SecondaryButton
                    href={getLocalizedPath(
                        "/csapatepites-budapest",
                        language
                    )}
                >
                    {text.companyButton}
                </SecondaryButton>
            </ContentSection>

            {/* ÚJ PROGRAM */}
            <ContentSection
                icon={
                    Sparkles
                }
                title={
                    text.differentTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.differentFirst}
                    </p>

                    <p>
                        {text.differentSecond}
                    </p>

                    <p>
                        {text.differentThird}
                    </p>
                </div>
            </ContentSection>

            {/* HELYSZÍN */}
            <ContentSection
                icon={
                    MapPin
                }
                title={
                    text.locationTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.locationDescription}
                    </p>

                    <p>
                        {text.locationSecond}
                    </p>
                </div>

                <SecondaryButton
                    href={
    language === "en"
        ? "/en/getting-here"
        : "/megkozelites"
}
                >
                    {text.locationButton}
                </SecondaryButton>
            </ContentSection>

            {/* ZÁRÓ BLOKK */}
            <section className="bg-black text-white">
                <div className="mx-auto flex min-h-[390px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-16 text-center md:max-w-5xl md:min-h-[500px] md:px-12 md:py-20">
                    <CalendarCheck
                        className="h-12 w-12 md:h-16 md:w-16"
                        strokeWidth={
                            2.2
                        }
                    />

                    <h2 className="mt-7 text-[27px] font-black lowercase leading-[1.1] md:text-[48px]">
                        {text.finalTitle}
                    </h2>

                    <p className="mt-7 max-w-[320px] text-[17px] font-black leading-[1.3] md:max-w-2xl md:text-[25px]">
                        {text.finalDescription}
                    </p>

                    <p className="mt-5 max-w-[310px] text-[15px] leading-[1.3] text-white/70 md:max-w-xl md:text-[20px]">
                        {text.finalSecond}
                    </p>

                    <PrimaryButton
                        href="/booking"
                    >
                        {text.finalButton}
                    </PrimaryButton>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function IconCircle({
    children,
}) {
    return (
        <div
            className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
            style={{
                width:
                    "80px",

                height:
                    "80px",

                minWidth:
                    "80px",

                maxWidth:
                    "80px",

                minHeight:
                    "80px",

                maxHeight:
                    "80px",

                boxSizing:
                    "border-box",
            }}
        >
            {children}
        </div>
    );
}

function ContentSection({
    icon: Icon,
    title,
    children,
    backgroundClass = "bg-[#dedede]",
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div className="mx-auto flex min-h-[470px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[590px] md:px-12 md:py-20">
                <IconCircle>
                    <Icon
                        style={{
                            width:
                                "38px",

                            height:
                                "38px",
                        }}
                        strokeWidth={
                            2.5
                        }
                    />
                </IconCircle>

                <h2 className="text-[21px] font-black lowercase leading-[1.1] md:text-[36px]">
                    {title}
                </h2>

                {children}
            </div>
        </section>
    );
}

function ListSection({
    icon: Icon,
    title,
    introduction,
    items,
}) {
    return (
        <section className="bg-[#dedede] text-black">
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                    <div className="flex flex-col items-center text-center">
                        <IconCircle>
                            <Icon
                                style={{
                                    width:
                                        "38px",

                                    height:
                                        "38px",
                                }}
                                strokeWidth={
                                    2.5
                                }
                            />
                        </IconCircle>

                        <h2 className="text-[22px] font-black lowercase leading-[1.1] md:text-[40px]">
                            {title}
                        </h2>

                        <p className="mt-6 max-w-[310px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                            {introduction}
                        </p>
                    </div>

                    <div className="mt-10 border-y-[3px] border-black md:mt-14">
                        {items.map(
                            (
                                item,
                                index
                            ) => (
                                <div
                                    key={
                                        item
                                    }
                                    className={`flex items-center gap-4 py-5 md:gap-6 md:py-6 ${index !==
                                        items.length - 1
                                        ? "border-b border-black/25"
                                        : ""
                                        }`}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                        {String(
                                            index +
                                            1
                                        ).padStart(
                                            2,
                                            "0"
                                        )}
                                    </span>

                                    <p className="text-[15px] font-semibold leading-[1.4] text-neutral-700 md:text-[19px]">
                                        {item}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function PrimaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className={`
                ${href === "/booking" ? "button-pulse" : ""} mt-9 flex h-[52px]
                min-w-[240px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                bg-[#f5a400] px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[280px]
                md:text-[21px]
            `}
        >
            {children}
        </a>
    );
}

function SecondaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className="
                mt-9 flex h-[52px]
                min-w-[220px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                border-[3px] border-black
                bg-transparent px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[260px]
                md:text-[21px]
            "
        >
            {children}
        </a>
    );
}

function PageImage({
    src,
    alt,
}) {
    return (
        <section className="w-full overflow-hidden bg-black">
            <div className="relative flex w-full items-center justify-center overflow-hidden bg-black">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 scale-[1.15] bg-cover bg-center blur-[45px]"
                    style={{
                        backgroundImage:
                            `url("${src}")`,
                    }}
                />

                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-black/20"
                />

                <img
                    src={
                        src
                    }
                    alt={
                        alt
                    }
                    className="relative z-10 block h-auto w-full max-w-[900px] object-contain"
                />
            </div>
        </section>
    );
}